import React, { type ReactNode, useEffect } from 'react';
import { useAuth } from '@context/AuthContext';
import { Navigate, useLocation } from 'react-router-dom';
import Layout from '@components/Layout/Layout';
import { showSpinner, hideSpinner } from '@components/Layout/LoadingSpinner';
import ContainerSizeController from '@components/Layout/ContainerSizeController';
import { buildAgreementRedirectPath } from '@/utils/agreements';

interface UserLayoutProps {
    children: ReactNode;
    title?: string;
    containerSize?: string; // Optional container size like "2200px"
}

const UserLayout: React.FC<UserLayoutProps> = ({ children, title = 'User - Project 314', containerSize }) => {
    const { isAuthenticated, isImpersonating, isLoading, requiresTwoFactorSetup, hasPendingRequiredAgreements, pendingRequiredAgreements } =
        useAuth();

    const location = useLocation();

    // Control loading spinner visibility based on authentication state
    useEffect(() => {
        if (isLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Always hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [isLoading]);

    // Show empty container while checking authentication
    if (isLoading) {
        return (
            <Layout title={title}>
                <ContainerSizeController responsivePadding size={containerSize} />
                <div className="container mx-auto mt-2 sm:mt-3 md:mt-4">
                    <div className="flex justify-center items-center" style={{ minHeight: '60vh' }}>
                        {/* LoadingSpinner is now controlled via the useEffect above */}
                    </div>
                </div>
            </Layout>
        );
    }

    // Only redirect if we're sure the user isn't authenticated (and we're done loading)
    if (!isAuthenticated) {
        // Preserve the entire URL including query parameters
        const currentUrl = encodeURIComponent(window.location.pathname + window.location.search);
        return <Navigate to={`/login?redirect=${currentUrl}`} />;
    }

    if (!isImpersonating && hasPendingRequiredAgreements) {
        return (
            <Navigate
                replace
                to={buildAgreementRedirectPath({
                    pendingRequiredAgreements,
                    redirectTarget: location.pathname + location.search
                })}
            />
        );
    }

    if (!isImpersonating && requiresTwoFactorSetup && location.pathname !== '/user/enable2fa') {
        const currentUrl = encodeURIComponent(location.pathname + location.search);
        return <Navigate replace to={`/user/enable2fa?redirect=${currentUrl}`} />;
    }

    return (
        <Layout title={title}>
            <ContainerSizeController responsivePadding size={containerSize} />
            <div className="container mx-auto mt-2 sm:mt-3 md:mt-4">
                <div className="flex flex-wrap">
                    <div className="w-full">{children}</div>
                </div>
            </div>
        </Layout>
    );
};

export default UserLayout;
